/**
 * THE DATA BAR — one kit for every bar that shows data: the card progress bar, the
 * schedule gauge and the cost range. The three jobs keep their own SCALE (progress is
 * a 0–100% fill, schedule is a behind←1.00→ahead gauge, cost is a money axis), but they
 * share ONE vocabulary — the same track, the same capped marker and value dot, the same
 * inline-number-then-hover rule, the same end labels — so they read as one product
 * (design-lead, 31 Oct; Vinnie approved the three scale/zone/range calls the same day).
 *
 * Every marker and dot is its own hover target on a wide invisible lane, so a 2px line is
 * easy to land on, and its key NUMBER shows inline — the hover only adds the meaning, it is
 * never where the figure first lives (discoverability is correctness). The popover wears the
 * app's real `.info-card` shape.
 */
import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type BarTip = { title: string; what: React.ReactNode };
/** A bar element's colour role — maps to the palette, never a raw hex. */
export type BarTone = 'behind' | 'ahead' | 'bad' | 'warn' | 'good' | 'neutral';

/** A reference line (expected %, 1.00 on-plan, the budget baseline) — a capped tick with
 *  its figure shown inline above it. `soft` draws it in the quieter ink. */
export type BarMarker = { pct: number; label?: string; soft?: boolean; tip?: BarTip };
/** A value dot — the index, the estimate. `value` is the number shown inline above it. */
export type BarDot = { pct: number; variant?: 'solid' | 'hollow'; tone?: BarTone; value?: string; tip?: BarTip };
/** A faint directional band on the track, so "behind vs ahead" reads without the end words. */
export type BarZone = { from: number; to: number; tone: 'behind' | 'ahead' };
/** The coloured span — a 0→to progress fill, or a from→to range (cost). */
export type BarFill = { from?: number; to: number; tone?: BarTone };
/** The caption under the track — directional words / axis ends, with an optional centred anchor. */
export type BarEnds = { left?: string; mid?: string; right?: string };

const clampPct = (p: number): number => Math.max(2, Math.min(98, p));
/** Which way an inline number grows so it never runs off either end of the track:
 *  near the left it hugs the marker and grows right, near the right it grows left. */
const inlineAlign = (pct: number): 'start' | 'mid' | 'end' => (pct > 82 ? 'end' : pct < 18 ? 'start' : 'mid');

/** One marker or dot, wrapped in its wide hover lane + the real info-card popover. The
 *  popover is PORTALED to the document body and positioned from the element's screen rect,
 *  so no ancestor's overflow or stacking can clip it (the bar lives in grid cells, card
 *  modals and drawers). It prefers to open above the element, and flips below when there is
 *  not enough room above — so it is never cut off by the top of the window or hidden behind
 *  a field above it — and clamps horizontally to stay on screen. An optional inline number
 *  sits above the element, edge-aware so it stays inside the track. */
function Hit({
  pct,
  tip,
  inline,
  children,
}: {
  pct: number;
  tip?: BarTip;
  inline?: { text: string; soft?: boolean };
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const popRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; below: boolean } | null>(null);

  const place = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pop = popRef.current;
    const pw = pop?.offsetWidth ?? 240;
    const ph = pop?.offsetHeight ?? 72;
    const gap = 8;
    // Open above by default; flip below when the popover would clear the top of the window.
    const below = r.top < ph + gap + 6;
    const top = below ? r.bottom + gap : r.top - gap - ph;
    const left = Math.max(8, Math.min(r.left + r.width / 2 - pw / 2, window.innerWidth - pw - 8));
    setPos({ top, left, below });
  }, []);

  useLayoutEffect(() => {
    if (!open) { setPos(null); return; }
    place();
    const id = requestAnimationFrame(place); // re-place once the popover has its real size
    const onMove = () => place();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, place]);

  return (
    <span
      ref={ref}
      className="dbar-hit"
      style={{ left: `${clampPct(pct)}%` }}
      tabIndex={tip ? 0 : -1}
      aria-label={tip ? tip.title : undefined}
      onMouseEnter={() => tip && setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => tip && setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {inline && <span className={`dbar-inline ${inlineAlign(pct)}${inline.soft ? ' soft' : ''}`}>{inline.text}</span>}
      {children}
      {tip && open && createPortal(
        <span
          ref={popRef}
          className={`dbar-pop${pos?.below ? ' below' : ''}`}
          role="note"
          style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
        >
          <span className="dbar-pop-t">{tip.title}</span>
          <span className="dbar-pop-b">{tip.what}</span>
        </span>,
        document.body,
      )}
    </span>
  );
}

export function DataBar({
  density = 'drill',
  fill,
  zones = [],
  markers = [],
  dots = [],
  ends,
  ariaLabel,
  className = '',
}: {
  /** `drill` = the roomy status-drill size; `tile` = the calm card-face size. */
  density?: 'drill' | 'tile';
  fill?: BarFill;
  zones?: BarZone[];
  markers?: BarMarker[];
  dots?: BarDot[];
  ends?: BarEnds;
  ariaLabel?: string;
  /** Appended to the root, so a caller can size the bar in its layout (e.g. flex into a cell). */
  className?: string;
}) {
  return (
    <div className={`dbar ${density}${className ? ' ' + className : ''}`} role="img" aria-label={ariaLabel}>
      <div className="dbar-lane">
        <div className="dbar-track">
          {zones.map((z, i) => (
            <span key={`z${i}`} className={`dbar-zone ${z.tone}`} style={{ left: `${z.from}%`, width: `${z.to - z.from}%` }} />
          ))}
          {fill && (
            <span
              className={`dbar-fill ${fill.tone ?? 'neutral'}`}
              style={{ left: `${fill.from ?? 0}%`, width: `${Math.max(0, fill.to - (fill.from ?? 0))}%` }}
            />
          )}
        </div>
        {markers.map((m, i) => (
          <Hit key={`m${i}`} pct={m.pct} tip={m.tip} inline={m.label ? { text: m.label, soft: true } : undefined}>
            <span className={`dbar-mark${m.soft ? ' soft' : ''}`}>
              <span className="dbar-cap" />
              <span className="dbar-stem" />
            </span>
          </Hit>
        ))}
        {dots.map((d, i) => (
          <Hit key={`d${i}`} pct={d.pct} tip={d.tip} inline={d.value ? { text: d.value } : undefined}>
            <span className={`dbar-dot ${d.tone ?? 'neutral'}${d.variant === 'hollow' ? ' hollow' : ''}`} />
          </Hit>
        ))}
      </div>
      {ends && (
        <div className="dbar-ends">
          <span>{ends.left}</span>
          {ends.mid && <span className="dbar-mid">{ends.mid}</span>}
          <span>{ends.right}</span>
        </div>
      )}
    </div>
  );
}
